import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLocalizedNavigate } from "@/hooks/useLocalizedPath";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useT } from "@/lib/translation";
import { abortAllPendingRequests } from "@/lib/api";
import { usePostHog } from "posthog-js/react";
import { getMe, getBillingStatus } from "@/api/client";
import { getIdentifyProps } from "@/lib/posthog";

// Only these prefixes require an authenticated session. A stale/invalid
// session cookie discovered while browsing anywhere else (marketing pages,
// public stock teaser pages, etc.) shouldn't bounce a visitor to /login —
// they were never trying to reach a protected resource.
const PROTECTED_PATH_PREFIXES = ["/portfolio", "/analysis"];

export const AuthListener = () => {
  const t = useT();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const isHandling = useRef(false);
  const lastRedirectTime = useRef(0);

  const isLoggedIn = document.cookie.includes("logged_in=true");
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: isLoggedIn,
    retry: false,
  });
  // Shares the "billing-info-status" query key with BillingGate/BillingInfoModal/
  // SubscriptionCard — react-query dedupes the fetch, this doesn't add a request.
  const { data: billingStatus } = useQuery({
    queryKey: ["billing-info-status"],
    queryFn: getBillingStatus,
    enabled: isLoggedIn,
  });

  useEffect(() => {
    if (me && posthog) {
      posthog.identify(
        me.id.toString(),
        getIdentifyProps(me.username, me.created_at, billingStatus),
      );
    }
  }, [me, billingStatus, posthog]);

  useEffect(() => {
    const handleExpiredSession = () => {
      if (isHandling.current) return;

      const now = Date.now();
      if (now - lastRedirectTime.current < 15000) return;

      isHandling.current = true;
      lastRedirectTime.current = now;

      const currentPath = window.location.pathname + window.location.search;
      const isProtectedPath = PROTECTED_PATH_PREFIXES.some((p) =>
        location.pathname.startsWith(p),
      );

      if (isProtectedPath) {
        // abortAllPendingRequests() shares one AbortController signal across
        // every authorizedFetch call. Only call it here — on a protected
        // route there really are in-flight authenticated requests worth
        // cancelling. On a public page (e.g. the stock teaser pages) this
        // would abort that page's own unrelated profile/financials/etc.
        // fetches mid-flight, which is exactly what caused the intermittent
        // "stuck loading, needs a refresh" bug when a stale `logged_in`
        // cookie triggered this background getMe check on every page load.
        abortAllPendingRequests();
        queryClient.clear();
        toast.warning(t("nav:auth.sessionExpired"));
        const redirectParam =
          currentPath !== "/login"
            ? `?redirect=${encodeURIComponent(currentPath)}`
            : "";
        navigate(`/login${redirectParam}`, { replace: true });
      }
      // On public pages, the stale cookie is already cleared by the 401
      // handler in lib/api.ts — nothing else to do, no disruptive redirect.

      setTimeout(() => {
        isHandling.current = false;
      }, 15000);
    };

    window.addEventListener("auth:unauthorized", handleExpiredSession);

    return () => {
      window.removeEventListener("auth:unauthorized", handleExpiredSession);
    };
  }, [navigate, queryClient, location.pathname, t]);

  return null;
};
